import {
  HttpBackend,
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpParams,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { ErrorService } from '@okcargo-apps/shared-application';
import { Observable, catchError, firstValueFrom, from, switchMap, tap, throwError } from 'rxjs';
import { JwtService } from '../auth/jwt.service';
import { CheckVersionService } from '../check-version';
import { IWebVersionInfo } from '../check-version/check-version.model';
import { APP_CONFIG, EnvironmentModel } from '../environment';
import { INCLUDE_AUTH_TOKEN } from './api.tokens';

const OVERRIDE_PRODUCTION_USERS = [
  {
    email: 'conductor@okcargo.io',
    phone: '666666670',
  },
];

const shouldOverrideLoginRequest = (req: HttpRequest<unknown>): boolean => {
  if (!req.url.includes('/auth/login')) return false;

  try {
    const body = req.body as { username?: string };
    if (!body?.username) return false;

    return OVERRIDE_PRODUCTION_USERS.some((user) => user.email === body.username || user.phone === body.username);
  } catch {
    return false;
  }
};

export const apiInterceptor: HttpInterceptorFn = (req, next) => {
  const config = inject(APP_CONFIG);
  const jwtService = inject(JwtService);
  const router = inject(Router);
  const errorService = inject(ErrorService);
  const checkVersionService = inject(CheckVersionService);
  const httpBackend = inject(HttpBackend);

  let revisedReq = req.clone();

  if (
    shouldOverrideLoginRequest(req) ||
    OVERRIDE_PRODUCTION_USERS.some((user) => user.email === jwtService.username || user.phone === jwtService.username)
  ) {
    revisedReq = req.clone({
      url: req.url.replace(config.apiUrl, config.apiDemoUrl),
    });
  }

  if (req.context.get(INCLUDE_AUTH_TOKEN)) {
    const authReq = addAuthToken(revisedReq, jwtService);
    return next(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return handle401Error(revisedReq, next, jwtService, router, config, httpBackend);
        } else {
          handleError(error, router, errorService, checkVersionService);
          return throwError(() => error);
        }
      }),
    );
  }

  return next(revisedReq).pipe(
    catchError((error: HttpErrorResponse) => {
      handleError(error, router, errorService, checkVersionService);
      return throwError(() => error);
    }),
  );
};

function addAuthToken(req: HttpRequest<unknown>, jwtService: JwtService): HttpRequest<unknown> {
  const accessToken = jwtService.accessToken;
  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
}

/**
 * Minimal error shape with the HTTP state, shared by the raw `HttpErrorResponse` and the
 * `HttpRequestError` that `HttpService` throws (the retried requests and the refresh go through it).
 */
type RequestError = { status?: number; message?: string };

/**
 * Whether the error is a rejection of the session itself.
 *
 * A network failure (status 0), a timeout or a server error is not: clearing the session for one of
 * those would log the user out, for good, because the connection blipped.
 */
function isSessionRejected(error: unknown): boolean {
  const status = (error as RequestError | undefined)?.status;

  return status === 401 || status === 403;
}

/** Ends the session and sends the user to the login. */
function endSession(jwtService: JwtService, router: Router): void {
  jwtService.clearTokens();
  // TODO review this
  router.navigate(['/login']);
}

/**
 * Asks for the version file (served by the backend host) and checks that it is what the backend
 * serves. It is used as a probe: a captive portal or a proxy commonly answers 401 (or a login page)
 * to requests that never reached the backend.
 */
async function canReachBackend(config: EnvironmentModel, httpBackend: HttpBackend): Promise<boolean> {
  try {
    const http = new HttpClient(httpBackend);
    const version = await firstValueFrom(
      http.get<IWebVersionInfo>(config.versionsUrl, { params: new HttpParams().set('cb', Date.now().toString()) }),
    );

    return !!version?.minVersion;
  } catch (error) {
    console.warn('[apiInterceptor] The backend could not be reached while checking the session:', error);

    return false;
  }
}

/**
 * Ends the session when the backend has really rejected it.
 *
 * Before ending it, the backend is probed: ending the session because of a 401 that never came from it
 * (a captive portal, a proxy, a connection that is not usable) would log the user out and destroy the
 * stored session because of the network.
 */
function rejectSessionOrKeepIt(
  jwtService: JwtService,
  router: Router,
  config: EnvironmentModel,
  httpBackend: HttpBackend,
  error: RequestError,
): Observable<never> {
  return from(canReachBackend(config, httpBackend)).pipe(
    switchMap((reachable) => {
      if (!reachable) {
        return throwError(() => error);
      }

      endSession(jwtService, router);

      return throwError(() => new Error(error.message));
    }),
  );
}

function handle401Error(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  jwtService: JwtService,
  router: Router,
  config: EnvironmentModel,
  httpBackend: HttpBackend,
): Observable<HttpEvent<unknown>> {
  return jwtService.requestNewAuthToken$().pipe(
    catchError((error: RequestError) => {
      if (!isSessionRejected(error)) {
        return throwError(() => error);
      }

      return rejectSessionOrKeepIt(jwtService, router, config, httpBackend, error);
    }),
    switchMap((newToken: string) => {
      jwtService.setAccessToken(newToken);
      const authReq = addAuthToken(req, jwtService);

      return next(authReq).pipe(
        catchError((error: RequestError) => {
          if (!isSessionRejected(error)) {
            return throwError(() => error);
          }

          // The retried request is rejected again: the refreshed token is not valid either.
          return rejectSessionOrKeepIt(jwtService, router, config, httpBackend, error);
        }),
      );
    }),
  );
}

// TODO implement this
function handleError(
  error: HttpErrorResponse,
  router: Router,
  errorService: ErrorService,
  checkVersionService: CheckVersionService,
): Observable<never> {
  if (error.status === 400 || error.status === 404 || error.status === 405) {
    return throwError(() => error);
  }

  // A network failure (status 0) is not a server error: the offline notice explains it and the app
  // stays where it is, instead of navigating to the error page (and, for authenticated requests,
  // instead of trying to refresh the session and clearing it).
  if (error.status === 0) {
    console.warn('[apiInterceptor] Network error:', error.message);

    return throwError(() => error);
  }

  const errorInfo = {
    message: error?.message,
    status: error?.status,
    detail: error?.error?.detail,
  };
  errorService.setError(errorInfo);

  return from(checkVersionService.forceCheck()).pipe(
    tap(() => {
      const needsUpdate = checkVersionService.isUpdateRequired();

      if (needsUpdate === true) {
        router.navigate(['/update-required'], { replaceUrl: true });
      } else {
        router.navigate(['/error'], { replaceUrl: true });
      }
    }),
    switchMap(() => throwError(() => error)),
  );
}
